import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import {
  AppBar, Toolbar, IconButton, Typography, Box, Avatar,
  Menu, Divider, Button, Dialog, DialogTitle,
  DialogContent, DialogActions, useMediaQuery,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import LogoutIcon from "@mui/icons-material/Logout";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { C, LAYOUT } from "../constants/colors";
import { NAV_ITEMS } from "../constants/nav";
import BrandLogo from "../components/BrandLogo";

export default function Navbar({ user, onLogout, onMenuClick }) {
  const location = useLocation();
  const isMobile = useMediaQuery("(max-width:900px)");
  const [anchorEl, setAnchorEl] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const current = NAV_ITEMS.find((n) => n.path === location.pathname);
  const initial = (user?.name || user?.email || "U").charAt(0).toUpperCase();

  const closeMenu = () => setAnchorEl(null);
  const requestSignOut = () => {
    closeMenu();
    setConfirmOpen(true);
  };
  const confirmSignOut = () => {
    setConfirmOpen(false);
    try { sessionStorage.setItem("ap_auth_notice", "signed_out"); } catch (_) {}
    onLogout();
  };

  return (
    <AppBar
      position="fixed"
      color="inherit"
      sx={{
        background: "rgba(255,255,255,0.92)",
        zIndex: (t) => t.zIndex.drawer + 1,
        borderBottom: `1px solid ${C.border}`,
        "&::after": {
          content: '""',
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
          height: 2,
          background: "linear-gradient(90deg, #173673 0%, #2E6BE6 55%, #3377FF 100%)",
          opacity: 0.9,
        },
      }}
    >
      <Toolbar sx={{ minHeight: `${LAYOUT.navbarHeight}px !important`, gap: 1.5 }}>
        {isMobile && (
          <IconButton edge="start" onClick={onMenuClick} sx={{ color: C.text }}>
            <MenuIcon />
          </IconButton>
        )}

        <Box sx={{ mr: 1.5, minWidth: 0 }}>
          <BrandLogo size={34} showWordmark={!isMobile} wordmarkSize={14.5} />
        </Box>

        <Typography sx={{ fontWeight: 700, fontSize: 13, color: C.muted, flex: 1, letterSpacing: "-0.01em" }}>
          {current?.label || ""}
        </Typography>

        <Box
          onClick={(e) => setAnchorEl(e.currentTarget)}
          sx={{
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 1.1,
            py: 0.45,
            borderRadius: 2,
            border: `1px solid ${C.border}`,
            background: C.surfaceAlt,
            "&:hover": { borderColor: C.borderStrong, background: "#fff" },
          }}
        >
          <Avatar sx={{
            width: 30, height: 30,
            bgcolor: C.primarySoft, color: C.primaryDark,
            fontSize: 13, fontWeight: 800,
          }}>
            {initial}
          </Avatar>
          {!isMobile && (
            <Box sx={{ minWidth: 0, pr: 0.25 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: C.text, lineHeight: 1.2 }} noWrap>
                {user?.name || "Account"}
              </Typography>
              <Typography sx={{ fontSize: 11, color: C.muted, lineHeight: 1.2 }} noWrap>
                {user?.email || ""}
              </Typography>
            </Box>
          )}
          <KeyboardArrowDownIcon sx={{ fontSize: 18, color: C.muted }} />
        </Box>

        <Menu
          anchorEl={anchorEl}
          open={!!anchorEl}
          onClose={closeMenu}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          PaperProps={{
            sx: {
              mt: 1,
              minWidth: 260,
              borderRadius: 2,
              border: `1px solid ${C.border}`,
              boxShadow: "0 12px 32px rgba(15,23,42,0.12)",
            },
          }}
        >
          <Box sx={{ px: 2, py: 1.5 }}>
            <Typography sx={{ fontWeight: 800, fontSize: 14, color: C.text }}>{user?.name || "Account"}</Typography>
            <Typography sx={{ fontSize: 12.5, color: C.muted }}>{user?.email}</Typography>
          </Box>
          <Divider />
          <Box sx={{ px: 2, py: 1.5 }}>
            <Button
              fullWidth
              variant="outlined"
              color="error"
              startIcon={<LogoutIcon />}
              onClick={requestSignOut}
              sx={{
                textTransform: "none",
                fontWeight: 800,
                borderRadius: 2,
                py: 1,
              }}
            >
              Sign out
            </Button>
          </Box>
        </Menu>

        <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ fontWeight: 800, pb: 0.5 }}>Sign out?</DialogTitle>
          <DialogContent>
            <Typography sx={{ color: C.muted, fontSize: 14.5, pt: 1 }}>
              You will need to sign in again to open schedules, assessments, and question banks.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
            <Button onClick={() => setConfirmOpen(false)} sx={{ fontWeight: 700 }}>
              Stay signed in
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={confirmSignOut}
              startIcon={<LogoutIcon />}
              sx={{ fontWeight: 800 }}
            >
              Sign out
            </Button>
          </DialogActions>
        </Dialog>
      </Toolbar>
    </AppBar>
  );
}
